import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import { API_ENDPOINTS } from '../../../../utils/config';
import DocumentModalShell from './DocumentModalShell';
import {
  parseCompanyProfile,
  mapApiLineItemToRow,
  defaultInvoiceLineRow,
  getInvoiceLineItems,
  computeDocumentTotal,
} from '../utils/documentModalHelpers';
import {
  extractCustomerDetails,
  extractClientDetails,
  getCustomerDisplayName,
} from '../utils/contactHelpers';
import { CheckCircle, AlertCircle, Plus, Trash2 } from 'lucide-react';

const getReadHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const InvoiceModal = ({ isOpen, onClose, onSave, invoice, mode = 'add', documentKind = 'invoice' }) => {
  const isEstimate = documentKind === 'estimate';
  const [formData, setFormData] = useState({
    customer: '',
    invoice_number: '',
    type: 'domestic',
    status: 'draft',
    invoice_date: '',
    due_date: '',
    tax_amount: 0.0,
    notes: '',
    terms: '',
    adjustment_type: 'discount',
    adjustment_amount: 0.0,
    template_type: 'inline',
    customer_name: '',
    customer_company: '',
    customer_address: '',
    customer_gstin: '',
    customer_pan: '',
  });

  const [contactType, setContactType] = useState('');
  const [items, setItems] = useState([defaultInvoiceLineRow('1')]);
  const itemIdRef = useRef(2);
  const billingDetailFetchedForInvoiceId = useRef(null);

  const [customers, setCustomers] = useState([]);
  const [clients, setClients] = useState([]);
  const [salesCatalogItems, setSalesCatalogItems] = useState([]);
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const computedSubtotal = useMemo(
    () =>
      items.reduce((sum, item) => {
        const q = item.item_type === 'service' ? 1 : parseFloat(item.quantity) || 1;
        const r = parseFloat(item.amount) || 0;
        return sum + q * r;
      }, 0),
    [items]
  );

  const computedTotal = computeDocumentTotal(
    computedSubtotal,
    formData.tax_amount,
    formData.adjustment_amount,
    formData.adjustment_type
  );

  useEffect(() => {
    if (isOpen) {
      fetchCustomers();
      fetchClients();
      fetchSalesCatalog();
      const token = localStorage.getItem('token');
      const headers = { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      axios
        .get(API_ENDPOINTS.COMPANY_PROFILE_LIST, { headers })
        .then((res) => setCompanyProfile(parseCompanyProfile(res.data)))
        .catch(() => setCompanyProfile(null));
    } else {
      setCustomersLoaded(false);
    }
  }, [isOpen]);

  const fetchSalesCatalog = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const response = await axios.get(API_ENDPOINTS.SALES_CATALOG_LIST, { headers });
      const data = Array.isArray(response.data) ? response.data : response.data?.results || [];
      setSalesCatalogItems(data);
    } catch {
      setSalesCatalogItems([]);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const response = await axios.get(API_ENDPOINTS.CLIENTS_LIST, { headers });
      const data = Array.isArray(response.data) ? response.data : response.data?.results || [];
      setClients(data);
    } catch {
      setClients([]);
    }
  };

  useEffect(() => {
    if (isOpen && customersLoaded) {
      if (invoice && mode === 'edit') {
        let customerId = '';
        if (invoice.customer) {
          if (typeof invoice.customer === 'object' && invoice.customer.id) {
            customerId = invoice.customer.id;
          } else if (typeof invoice.customer === 'number' || typeof invoice.customer === 'string') {
            customerId = invoice.customer;
          }
        }
        let clientId = '';
        if (invoice.client) {
          if (typeof invoice.client === 'object' && invoice.client.id) {
            clientId = invoice.client.id;
          } else if (typeof invoice.client === 'number' || typeof invoice.client === 'string') {
            clientId = invoice.client;
          }
        }

        const ct = clientId ? 'client' : 'customer';
        const contactId = clientId || customerId;

        let billingFromNested = {};
        if (invoice.client && typeof invoice.client === 'object') {
          const d = extractClientDetails(invoice.client);
          billingFromNested = {
            customer_name: d.name,
            customer_company: d.company,
            customer_address: d.address,
            customer_gstin: d.gstin,
            customer_pan: d.pan,
          };
        } else if (invoice.customer && typeof invoice.customer === 'object') {
          const d = extractCustomerDetails(invoice.customer);
          billingFromNested = {
            customer_name: d.name,
            customer_company: d.company,
            customer_address: d.address,
            customer_gstin: d.gstin,
            customer_pan: d.pan,
          };
        }

        setContactType(ct);
        setFormData({
          customer: contactId ? String(contactId) : '',
          invoice_number: invoice.invoice_number || '',
          type: invoice.type || 'domestic',
          status: invoice.status || 'draft',
          invoice_date: invoice.invoice_date ? invoice.invoice_date.split('T')[0] : '',
          due_date: invoice.due_date ? invoice.due_date.split('T')[0] : '',
          tax_amount: parseFloat(invoice.tax_amount || 0),
          notes: invoice.notes || '',
          terms: invoice.terms || '',
          adjustment_type: invoice.adjustment_type || 'discount',
          adjustment_amount: parseFloat(invoice.adjustment_amount || 0),
          template_type: invoice.template_type || 'inline',
          customer_name:
            billingFromNested.customer_name || invoice.customer_name || invoice.client_name || '',
          customer_company:
            billingFromNested.customer_company || invoice.customer_company || invoice.client_company || '',
          customer_address:
            billingFromNested.customer_address || invoice.customer_address || invoice.client_address || '',
          customer_gstin:
            billingFromNested.customer_gstin || invoice.customer_gstin || invoice.client_gstin || '',
          customer_pan: billingFromNested.customer_pan || invoice.customer_pan || invoice.client_pan || '',
        });

        const lines = getInvoiceLineItems(invoice);
        if (lines.length > 0) {
          setItems(lines.map((row, idx) => mapApiLineItemToRow(row, idx)));
          itemIdRef.current = lines.length + 1;
        } else {
          const id = `new-${Date.now()}`;
          setItems([defaultInvoiceLineRow(id)]);
          itemIdRef.current = 2;
        }
      } else {
        const today = new Date().toISOString().split('T')[0];
        const nextMonth = new Date();
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        const dueDate = nextMonth.toISOString().split('T')[0];
        setContactType('');
        setFormData((prev) => ({
          ...prev,
          customer: '',
          invoice_number: '',
          invoice_date: today,
          due_date: dueDate,
          tax_amount: 0,
          adjustment_amount: 0,
          notes: '',
          terms: '',
          customer_name: '',
          customer_company: '',
          customer_address: '',
          customer_gstin: '',
          customer_pan: '',
        }));
        const id = `new-${Date.now()}`;
        setItems([defaultInvoiceLineRow(id)]);
        itemIdRef.current = 2;
      }
    }
  }, [isOpen, invoice, mode, customersLoaded]);

  useEffect(() => {
    if (!isOpen) {
      billingDetailFetchedForInvoiceId.current = null;
      return;
    }
    if (mode !== 'edit' || !invoice?.id || !customersLoaded) return;
    if (billingDetailFetchedForInvoiceId.current === invoice.id) return;

    let cid = '';
    let ctype = '';
    if (invoice.client) {
      const id = typeof invoice.client === 'object' ? invoice.client.id : invoice.client;
      if (id != null && id !== '') {
        cid = String(id);
        ctype = 'client';
      }
    }
    if (!cid && invoice.customer) {
      const id = typeof invoice.customer === 'object' ? invoice.customer.id : invoice.customer;
      if (id != null && id !== '') {
        cid = String(id);
        ctype = 'customer';
      }
    }
    if (!cid) {
      billingDetailFetchedForInvoiceId.current = invoice.id;
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const headers = getReadHeaders();
        if (ctype === 'customer') {
          const { data } = await axios.get(API_ENDPOINTS.CUSTOMERS_DETAIL(cid), { headers });
          if (cancelled) return;
          const d = extractCustomerDetails(data);
          setFormData((prev) => ({
            ...prev,
            customer: cid,
            customer_name: d.name || prev.customer_name,
            customer_company: d.company || prev.customer_company,
            customer_address: d.address || prev.customer_address,
            customer_gstin: d.gstin || prev.customer_gstin,
            customer_pan: d.pan || prev.customer_pan,
          }));
        } else {
          const { data } = await axios.get(API_ENDPOINTS.CLIENTS_DETAIL(cid), { headers });
          if (cancelled) return;
          const d = extractClientDetails(data);
          setFormData((prev) => ({
            ...prev,
            customer: cid,
            customer_name: d.name || prev.customer_name,
            customer_company: d.company || prev.customer_company,
            customer_address: d.address || prev.customer_address,
            customer_gstin: d.gstin || prev.customer_gstin,
            customer_pan: d.pan || prev.customer_pan,
          }));
        }
      } catch {
        /* keep nested / flat invoice fields */
      } finally {
        if (!cancelled) billingDetailFetchedForInvoiceId.current = invoice.id;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, customersLoaded, invoice]);

  const fetchCustomers = async () => {
    try {
      setCustomersLoaded(false);
      const token = localStorage.getItem('token');
      const headers = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const response = await axios.get(API_ENDPOINTS.CUSTOMERS_LIST, { headers });
      const customersData = Array.isArray(response.data) ? response.data : response.data?.results || [];
      setCustomers(customersData);
      setCustomersLoaded(true);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      setCustomersLoaded(true);
    }
  };

  const handleContactTypeChange = (value) => {
    setContactType(value);
    setFormData((prev) => ({
      ...prev,
      customer: '',
      customer_name: '',
      customer_company: '',
      customer_address: '',
      customer_gstin: '',
      customer_pan: '',
    }));
  };

  const applyBillingDetails = (d, contactId) => {
    setFormData((prev) => ({
      ...prev,
      customer: contactId,
      customer_name: d.name || prev.customer_name,
      customer_company: d.company || '',
      customer_address: d.address || '',
      customer_gstin: d.gstin || '',
      customer_pan: d.pan || '',
    }));
  };

  const handleContactSelectChange = async (contactId) => {
    if (!contactId) {
      setFormData((prev) => ({ ...prev, customer: '' }));
      return;
    }
    if (!contactType) {
      setFormData((prev) => ({ ...prev, customer: contactId }));
      return;
    }

    if (contactType === 'customer') {
      const customer = customers.find((c) => String(c.id) === String(contactId));
      if (customer) {
        applyBillingDetails(extractCustomerDetails(customer), contactId);
        return;
      }
      try {
        const { data } = await axios.get(API_ENDPOINTS.CUSTOMERS_DETAIL(contactId), {
          headers: getReadHeaders(),
        });
        applyBillingDetails(extractCustomerDetails(data), contactId);
      } catch {
        setFormData((prev) => ({ ...prev, customer: contactId }));
      }
      return;
    }

    const client = clients.find((c) => String(c.id) === String(contactId));
    if (client) {
      applyBillingDetails(extractClientDetails(client), contactId);
      return;
    }
    try {
      const { data } = await axios.get(API_ENDPOINTS.CLIENTS_DETAIL(contactId), {
        headers: getReadHeaders(),
      });
      applyBillingDetails(extractClientDetails(data), contactId);
    } catch {
      setFormData((prev) => ({ ...prev, customer: contactId }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleBillingFieldChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      const row = { ...next[index] };
      if (field === 'quantity' || field === 'amount') {
        row[field] = field === 'quantity' ? parseFloat(value) || 1 : parseFloat(value) || 0;
      } else if (field === 'item_type') {
        row.item_type = value;
        if (value === 'service') {
          row.quantity = 1;
          row.hsn_code = '';
        } else {
          row.sac_code = '';
        }
      } else {
        row[field] = value;
      }
      next[index] = row;
      return next;
    });
  };

  const addItem = () => {
    const id = `new-${itemIdRef.current++}`;
    setItems((prev) => [...prev, defaultInvoiceLineRow(id)]);
  };

  const removeItem = (index) => {
    setItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!contactType) newErrors.customer = 'Contact type is required';
    if (!formData.customer) newErrors.customer = 'Select a customer or client';
    if (!formData.invoice_date) {
      newErrors.invoice_date = isEstimate ? 'Estimate date is required' : 'Invoice date is required';
    }
    if (!formData.due_date) newErrors.due_date = 'Due date is required';
    if (formData.tax_amount < 0) newErrors.tax_amount = 'Tax amount cannot be negative';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildItemsPayload = () =>
    items.map((item) => {
      const qty = item.item_type === 'service' ? 1 : parseFloat(item.quantity) || 1;
      const rate = parseFloat(item.amount) || 0;
      const itemAmount = qty * rate;
      const tr = parseFloat(item.tax_rate) || 0;
      let ta = parseFloat(item.tax_amount);
      if (!Number.isFinite(ta)) {
        ta = (itemAmount * tr) / 100;
      }
      const base = {
        sac_code: item.item_type === 'service' ? item.sac_code || '' : '',
        hsn_code: item.item_type === 'goods' ? item.hsn_code || '' : '',
        description: item.description || '',
        quantity: qty,
        rate,
        amount: itemAmount,
        tax_rate: tr,
        tax_amount: ta,
      };
      if (typeof item.id === 'number') {
        return { id: item.id, ...base };
      }
      return base;
    });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const subtotal = computedSubtotal;
      const total_amount = computeDocumentTotal(
        subtotal,
        formData.tax_amount,
        formData.adjustment_amount,
        formData.adjustment_type
      );

      let customer_name = formData.customer_name;
      if (!String(customer_name || '').trim() && formData.customer) {
        if (contactType === 'customer') {
          const c = customers.find((x) => String(x.id) === String(formData.customer));
          if (c) customer_name = getCustomerDisplayName(c);
        } else if (contactType === 'client') {
          const c = clients.find((x) => String(x.id) === String(formData.customer));
          if (c) customer_name = c.name || c.client_name || c.company_name || '';
        }
      }

      const payload = {
        invoice_number: formData.invoice_number || null,
        type: formData.type,
        status: formData.status,
        invoice_date: formData.invoice_date,
        due_date: formData.due_date,
        subtotal,
        tax_amount: parseFloat(formData.tax_amount),
        total_amount,
        notes: formData.notes,
        terms: formData.terms || '',
        adjustment_type: formData.adjustment_type,
        adjustment_amount: parseFloat(formData.adjustment_amount),
        template_type: formData.template_type,
        customer_name: customer_name || formData.customer_name,
        customer_company: formData.customer_company,
        customer_address: formData.customer_address,
        customer_gstin: formData.customer_gstin,
        customer_pan: formData.customer_pan,
        customer: contactType === 'customer' && formData.customer ? parseInt(formData.customer, 10) : null,
        client: contactType === 'client' && formData.customer ? parseInt(formData.customer, 10) : null,
        items: buildItemsPayload(),
      };

      if (mode === 'edit' && invoice?.id) {
        const updateUrl = isEstimate
          ? API_ENDPOINTS.ESTIMATES_UPDATE(invoice.id)
          : API_ENDPOINTS.INVOICES_UPDATE(invoice.id);
        await axios.put(updateUrl, payload, { headers });
      } else {
        const createUrl = isEstimate ? API_ENDPOINTS.ESTIMATES_CREATE : API_ENDPOINTS.INVOICES_CREATE;
        await axios.post(createUrl, payload, { headers });
      }

      setSuccessMessage(
        mode === 'edit'
          ? isEstimate
            ? 'Estimate updated successfully'
            : 'Invoice updated successfully'
          : isEstimate
            ? 'Estimate created successfully'
            : 'Invoice created successfully'
      );
      setTimeout(() => {
        onSave();
        onClose();
      }, 1500);
    } catch (error) {
      const apiError =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        (isEstimate ? 'Failed to save estimate' : 'Failed to save invoice');
      setErrorMessage(apiError);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const docNumberLabel = isEstimate ? 'Estimate Number' : 'Invoice Number';
  const docDateLabel = isEstimate ? 'Estimate Date' : 'Invoice Date';
  const submitLabel = isSubmitting
    ? 'Saving...'
    : mode === 'edit'
      ? isEstimate
        ? 'Update Estimate'
        : 'Update Invoice'
      : isEstimate
        ? 'Create Estimate'
        : 'Create Invoice';

  return (
    <DocumentModalShell
      title={mode === 'edit' ? (isEstimate ? 'Edit Estimate' : 'Edit Invoice') : isEstimate ? 'Create Estimate' : 'Create Invoice'}
      onClose={onClose}
      banner={isEstimate ? 'ESTIMATE' : 'TAX INVOICE'}
      companyProfile={companyProfile}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>{errorMessage}</span>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>{successMessage}</span>
          </div>
        )}

        {/* Document details | Bill To — same grid as TaxInvoiceForm */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{docNumberLabel}</label>
              <input
                type="text"
                name="invoice_number"
                value={formData.invoice_number}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Auto-generated if empty"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{docDateLabel}</label>
              <input
                type="date"
                name="invoice_date"
                value={formData.invoice_date}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors.invoice_date ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.invoice_date && <p className="mt-1 text-sm text-red-600">{errors.invoice_date}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors.due_date ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.due_date && <p className="mt-1 text-sm text-red-600">{errors.due_date}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="domestic">Domestic</option>
                <option value="export">Export</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground">Bill To</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Type</label>
              <select
                value={contactType}
                onChange={(e) => handleContactTypeChange(e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors.customer ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select Type</option>
                <option value="customer">Customer</option>
                <option value="client">Client</option>
              </select>
            </div>
            {contactType && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {contactType === 'customer' ? 'Select Customer' : 'Select Client'}
                </label>
                <select
                  value={String(formData.customer || '')}
                  onChange={(e) => handleContactSelectChange(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                    errors.customer ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select {contactType === 'customer' ? 'Customer' : 'Client'}</option>
                  {contactType === 'customer'
                    ? customers.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {getCustomerDisplayName(c)}
                        </option>
                      ))
                    : clients.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {c.name || c.client_name || c.company_name || `Client ${c.id}`}
                        </option>
                      ))}
                  {mode === 'edit' &&
                    invoice &&
                    formData.customer &&
                    !(contactType === 'customer'
                      ? customers.find((c) => String(c.id) === String(formData.customer))
                      : clients.find((c) => String(c.id) === String(formData.customer))) &&
                    (invoice.customer_name || invoice.client_name) && (
                      <option value={String(formData.customer)}>
                        {invoice.customer_name || invoice.client_name}
                      </option>
                    )}
                </select>
                {errors.customer && <p className="mt-1 text-sm text-red-600">{errors.customer}</p>}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <input
                type="text"
                value={formData.customer_company}
                onChange={(e) => handleBillingFieldChange('customer_company', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input
                type="text"
                value={formData.customer_address}
                onChange={(e) => handleBillingFieldChange('customer_address', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                <input
                  type="text"
                  value={formData.customer_gstin}
                  onChange={(e) => handleBillingFieldChange('customer_gstin', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PAN</label>
                <input
                  type="text"
                  value={formData.customer_pan}
                  onChange={(e) => handleBillingFieldChange('customer_pan', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Line items — match TaxInvoiceForm table */}
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">Services</h3>
          <div
            className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 tax-invoice-table-scroll"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}
          >
            <div className="inline-block min-w-full align-middle">
              <table className="min-w-[640px] sm:min-w-full w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">
                      S.No
                    </th>
                    <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">
                      Item type
                    </th>
                    <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">
                      SAC / HSN Code
                    </th>
                    <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium min-w-[150px]">
                      Description of Goods / Services
                    </th>
                    <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">
                      Quantity
                    </th>
                    <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">
                      Amount (₹)
                    </th>
                    <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={item.id}>
                      <td className="border border-gray-300 px-2 sm:px-4 py-2 text-xs sm:text-sm">{index + 1}</td>
                      <td className="border border-gray-300 px-2 sm:px-4 py-2">
                        <select
                          value={item.item_type}
                          onChange={(e) => handleItemChange(index, 'item_type', e.target.value)}
                          className="w-full min-w-[88px] px-2 py-1 text-xs sm:text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="goods">Goods</option>
                          <option value="service">Service</option>
                        </select>
                      </td>
                      <td className="border border-gray-300 px-2 sm:px-4 py-2">
                        <input
                          type="text"
                          value={item.item_type === 'service' ? item.sac_code : item.hsn_code}
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              item.item_type === 'service' ? 'sac_code' : 'hsn_code',
                              e.target.value
                            )
                          }
                          className="w-full min-w-[80px] px-2 py-1 text-xs sm:text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary"
                          placeholder={item.item_type === 'service' ? 'SAC' : 'HSN'}
                        />
                      </td>
                      <td className="border border-gray-300 px-2 sm:px-4 py-2">
                        <input
                          type="text"
                          list={`invoice-line-catalog-${item.id}`}
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          className="w-full min-w-[150px] px-2 py-1 text-xs sm:text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary"
                          placeholder="Description or pick from catalog"
                        />
                        <datalist id={`invoice-line-catalog-${item.id}`}>
                          {salesCatalogItems.map((catalogItem) => {
                            const catalogData =
                              catalogItem.catalog_data ||
                              catalogItem.goods_data ||
                              catalogItem.services_data ||
                              {};
                            const itemName = catalogData.goods_name || catalogData.service_name || '';
                            if (!itemName) return null;
                            return <option key={catalogItem.id} value={itemName} />;
                          })}
                        </datalist>
                      </td>
                      <td className="border border-gray-300 px-2 sm:px-4 py-2">
                        {item.item_type === 'service' ? (
                          <span className="text-xs sm:text-sm text-gray-500">Not required</span>
                        ) : (
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            min="1"
                            className="w-full min-w-[60px] px-2 py-1 text-xs sm:text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                        )}
                      </td>
                      <td className="border border-gray-300 px-2 sm:px-4 py-2">
                        <input
                          type="number"
                          value={item.amount}
                          onChange={(e) => handleItemChange(index, 'amount', e.target.value)}
                          min="0"
                          step="0.01"
                          className="w-full min-w-[80px] px-2 py-1 text-xs sm:text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                        <div className="text-[11px] sm:text-xs text-gray-500 mt-1">
                          Line total: ₹
                          {(item.amount * (item.item_type === 'service' ? 1 : item.quantity || 1)).toFixed(2)}
                        </div>
                      </td>
                      <td className="border border-gray-300 px-2 sm:px-4 py-2">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Button type="button" variant="outline" onClick={addItem} className="mt-3 sm:mt-4 w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
        </div>

        {/* Amount summary — same block style as TaxInvoiceForm */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-700">Total Taxable Value:</span>
                  <span className="font-semibold">
                    ₹
                    {computedSubtotal.toLocaleString('en-IN', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-700">Tax Amount:</span>
                  <input
                    type="number"
                    name="tax_amount"
                    value={formData.tax_amount}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className={`w-32 px-2 py-1 text-right border rounded-md text-sm font-semibold ${
                      errors.tax_amount ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
                <div className="flex justify-between items-center gap-2">
                  <span className="text-sm text-gray-700 shrink-0">Adjustment</span>
                  <div className="flex gap-2 items-center flex-wrap justify-end">
                    <select
                      name="adjustment_type"
                      value={formData.adjustment_type}
                      onChange={handleChange}
                      className="px-2 py-1 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="discount">Discount</option>
                      <option value="surcharge">Surcharge</option>
                    </select>
                    <input
                      type="number"
                      name="adjustment_amount"
                      value={formData.adjustment_amount}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      className="w-28 px-2 py-1 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-300">
                  <span className="font-semibold">Total Amount:</span>
                  <span className="font-semibold">
                    ₹
                    {computedTotal.toLocaleString('en-IN', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Template Type</label>
              <select
                name="template_type"
                value={formData.template_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="inline">Inline</option>
                <option value="standard">Standard</option>
              </select>
            </div>
          </div>
          {isEstimate ? (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Bank Details for Payment</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600">Account Name:</span>{' '}
                    <span>{companyProfile?.accountName || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Bank Name:</span> <span>{companyProfile?.bankName || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Account No:</span> <span>{companyProfile?.accountNo || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">IFSC:</span> <span>{companyProfile?.ifscCode || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Branch:</span> <span>{companyProfile?.branch || '—'}</span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Signature</h3>
                <div className="text-sm text-gray-600">
                  <p>For {companyProfile?.name || 'COMPANY NAME'}</p>
                  <div className="mt-8 border-t border-gray-300 pt-2">
                    <p>Authorized Signatory</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Additional notes..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Terms</label>
          <textarea
            name="terms"
            value={formData.terms}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Payment terms..."
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </DocumentModalShell>
  );
};

export default InvoiceModal;
